import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import FreshnessBadge from "@/components/FreshnessBadge";
import {
  Activity,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  FileText,
  Clock,
  Server,
  Hash,
  Zap,
} from "lucide-react";

const STATUS_STYLES = {
  SUCCESS: { color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", icon: CheckCircle2, label: "Successo" },
  WARNING: { color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", icon: AlertTriangle, label: "Warning" },
  FAILED: { color: "text-red-700", bg: "bg-red-50", border: "border-red-200", icon: XCircle, label: "Fallito" },
  DUPLICATE: { color: "text-slate-600", bg: "bg-slate-50", border: "border-slate-200", icon: Hash, label: "Duplicato" },
  DRY_RUN: { color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200", icon: Zap, label: "Dry run" },
  PENDING: { color: "text-slate-500", bg: "bg-slate-50", border: "border-slate-200", icon: Clock, label: "In corso" },
};

// Etichette source neutrali per l'interfaccia — nasconde i valori tecnici del backend
// e i riferimenti al provider dati storico.
const SOURCE_LABELS = {
  adybo_market_bridge: "Adybo Market Bridge",
  montel_excel: "Adybo Market Bridge", // legacy alias
  manual: "Import manuale",
  test: "Test",
};

const humanSource = (s) => SOURCE_LABELS[s] || "Adybo Market Bridge";

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.PENDING;
  const Icon = s.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.color} border ${s.border}`}
      data-testid={`mi-status-${status}`}
    >
      <Icon size={12} /> {s.label}
    </span>
  );
}

function StatusCard({ title, batch, tone = "neutral", testId }) {
  const s = batch ? STATUS_STYLES[batch.status] || STATUS_STYLES.PENDING : null;
  const border =
    tone === "success" ? "border-emerald-300" : tone === "danger" ? "border-red-300" : tone === "warning" ? "border-amber-300" : "border-slate-200";
  return (
    <div className={`card-elevated p-5 border-2 ${border}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</div>
        {batch && <StatusBadge status={batch.status} />}
      </div>
      {!batch ? (
        <div className="text-sm text-slate-400 italic">Nessun record</div>
      ) : (
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center gap-2 text-slate-800">
            <FileText size={13} className="text-blue-600" />
            <span className="font-semibold truncate">{batch.filename}</span>
          </div>
          <div className="text-xs text-slate-500">
            <b>Fonte:</b> {batch.source ? humanSource(batch.source) : "—"}
          </div>
          <div className="text-xs text-slate-500">
            <b>Batch:</b> <span className="font-mono">{(batch.batch_id || "").slice(0, 8)}…</span>
          </div>
          <div className="text-xs text-slate-500">
            <b>Ricevuto:</b> {fmtDateTime(batch.received_at)}
          </div>
          {batch.completed_at && (
            <div className="text-xs text-slate-500">
              <b>Completato:</b> {fmtDateTime(batch.completed_at)}
              {batch.duration_ms != null && (
                <span className="text-slate-400"> · {batch.duration_ms} ms</span>
              )}
            </div>
          )}
          {batch.row_count != null && (
            <div className="text-xs text-slate-500">
              <b>Record:</b> {batch.row_count}
              {batch.market_prices_count != null && (
                <span className="text-slate-400">
                  {" "}
                  (prezzi {batch.market_prices_count} · indicatori {batch.dashboard_indicators_count || 0})
                </span>
              )}
            </div>
          )}
          {batch.warnings && batch.warnings.length > 0 && (
            <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
              <b>Warning:</b>
              <ul className="list-disc pl-4 mt-0.5">
                {batch.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          {batch.error_message && (
            <div className="mt-2 text-xs bg-red-50 border border-red-200 rounded p-2 text-red-800">
              <b>Errore:</b> {batch.error_message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminMarketImport() {
  const [status, setStatus] = useState(null);
  const [batches, setBatches] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [s, b] = await Promise.all([
        api.get("/admin/market-import/status"),
        api.get("/admin/market-import/batches", { params: filter ? { status: filter, limit: 50 } : { limit: 50 } }),
      ]);
      setStatus(s.data);
      setBatches(b.data.batches || []);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);
  // Auto-refresh 30s + refresh immediato quando l'utente torna sulla scheda
  useAutoRefresh(load, 30_000);

  const lastPublished = status?.last_published;
  const lastFailed = status?.last_failed;
  const lastReceived = status?.last_received;
  const consecutiveFailures = status?.consecutive_failures || 0;

  return (
    <div className="space-y-6" data-testid="admin-market-import-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
            Caricamento Excel Automatico
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Storico degli import automatici da <b>Adybo Market Bridge</b> (aggiornamento automatico dati mercato).
            L&apos;import manuale rimane disponibile su <a href="/admin/upload" className="text-blue-600 hover:underline">Caricamento Excel</a>.
          </p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50"
          data-testid="mi-refresh-btn"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Aggiorna
        </button>
      </div>

      {status?.freshness && <FreshnessBadge freshness={status.freshness} />}

      {consecutiveFailures >= 2 && (
        <div className="rounded-xl border-2 border-red-300 bg-red-50 px-4 py-3 flex items-start gap-3" data-testid="mi-consecutive-alert">
          <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">
            <b>{consecutiveFailures} fallimenti consecutivi.</b> Verificare il PC Windows con Adybo Market Bridge (aggiornamento automatico dati mercato).
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Caricamento…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatusCard
              title="Ultimo pubblicato"
              batch={lastPublished}
              tone={lastPublished ? "success" : "neutral"}
              testId="mi-card-published"
            />
            <StatusCard
              title="Ultimo ricevuto"
              batch={lastReceived}
              tone={lastReceived?.status === "FAILED" ? "danger" : lastReceived?.status === "WARNING" ? "warning" : "neutral"}
              testId="mi-card-received"
            />
            <StatusCard
              title="Ultimo fallito"
              batch={lastFailed}
              tone={lastFailed ? "danger" : "neutral"}
              testId="mi-card-failed"
            />
          </div>

          <div className="card-elevated overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
              <div className="font-display font-semibold flex items-center gap-2">
                <Activity size={16} className="text-blue-600" /> Storico importazioni
                <span className="text-xs font-normal text-slate-400">({batches.length})</span>
              </div>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-blue-500"
                data-testid="mi-filter-status"
              >
                <option value="">Tutti gli stati</option>
                <option value="SUCCESS">Successo</option>
                <option value="WARNING">Warning</option>
                <option value="FAILED">Fallito</option>
                <option value="DUPLICATE">Duplicato</option>
                <option value="DRY_RUN">Dry run</option>
              </select>
            </div>
            {batches.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500">Nessuna importazione registrata.</div>
            ) : (
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th>Ricevuto</th>
                    <th>Filename</th>
                    <th>Fonte</th>
                    <th>Batch</th>
                    <th className="text-right">Record</th>
                    <th className="text-right">Durata</th>
                    <th>Stato</th>
                    <th>Warning/Errore</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.id} data-testid={`mi-row-${b.batch_id}`}>
                      <td className="text-xs whitespace-nowrap">{fmtDateTime(b.received_at)}</td>
                      <td className="!font-sans text-sm">
                        <FileText size={12} className="inline mr-1 text-blue-600" />
                        {b.filename}
                      </td>
                      <td className="!font-sans text-xs text-slate-500">
                        <Server size={11} className="inline mr-1" />
                        {humanSource(b.source)}
                      </td>
                      <td className="!font-mono text-[10px] text-slate-500" title={b.batch_id}>
                        {(b.batch_id || "").slice(0, 8)}…
                      </td>
                      <td className="text-right text-xs">
                        {b.row_count ?? 0}
                      </td>
                      <td className="text-right text-xs text-slate-500">
                        {b.duration_ms != null ? `${b.duration_ms} ms` : "—"}
                      </td>
                      <td>
                        <StatusBadge status={b.status} />
                      </td>
                      <td className="text-xs text-slate-600 max-w-md">
                        {b.error_message && (
                          <div className="text-red-700 truncate" title={b.error_message}>
                            {b.error_message}
                          </div>
                        )}
                        {b.warnings && b.warnings.length > 0 && (
                          <div className="text-amber-700 truncate" title={b.warnings.join(" · ")}>
                            {b.warnings.slice(0, 2).join(" · ")}
                            {b.warnings.length > 2 && ` (+${b.warnings.length - 2})`}
                          </div>
                        )}
                        {!b.error_message && (!b.warnings || b.warnings.length === 0) && (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
